import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cartApi } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useLocalization } from '../contexts/LocalizationContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cartCount, setCartCount] = useState(0);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const fetchSeq = useRef(0);
  const { toast } = useToast();
  const { t } = useTranslation();
  const { countryCode, loading: localizationLoading } = useLocalization();

  // Generate or get session ID
  const getSessionId = () => {
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      localStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  };

  // Fetch cart from backend
  const fetchCart = async () => {
    try {
      const sessionId = getSessionId();
      const seq = ++fetchSeq.current;
      const data = await cartApi.getCart(sessionId, countryCode);
      if (seq !== fetchSeq.current) return; // a newer request (e.g. after a country change) supersedes this one
      setCart(data);
      setCartCount(data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCart({ items: [], subtotal: 0 });
      setCartCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Add item to cart
  const addToCart = async (productId, quantity = 1, variantId = null) => {
    try {
      const sessionId = getSessionId();
      await cartApi.addToCart(productId, quantity, variantId, sessionId, countryCode);
      await fetchCart();
      
      // Open cart sidebar automatically
      setIsCartOpen(true);
      
      toast({
        title: t('cart.toast.addedTitle'),
        description: t('cart.toast.addedDescription'),
      });
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Handle error message properly
      let errorMsg = t('cart.toast.addFailed');

      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMsg = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMsg = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'object') {
          // Structured error from our stock guard: {code, message, available}
          errorMsg = detail.message || detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive',
      });
      return false;
    }
  };

  // Update cart item quantity
  const updateCartItem = async (productId, quantity, variantId = null) => {
    try {
      const sessionId = getSessionId();
      await cartApi.updateCartItem(productId, quantity, variantId, sessionId, countryCode);
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error updating cart:', error);
      toast({
        title: t('common.error'),
        description: t('cart.toast.updateFailed'),
        variant: 'destructive',
      });
      return false;
    }
  };

  // Remove item from cart
  const removeFromCart = async (productId, variantId = null) => {
    try {
      const sessionId = getSessionId();
      await cartApi.removeFromCart(productId, variantId, sessionId);
      await fetchCart();
      toast({
        title: t('cart.toast.removedTitle'),
        description: t('cart.toast.removedDescription'),
      });
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      toast({
        title: t('common.error'),
        description: t('cart.toast.removeFailed'),
        variant: 'destructive',
      });
      return false;
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      const sessionId = getSessionId();
      await cartApi.clearCart(sessionId);
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  };

  useEffect(() => {
    // Wait for the storefront country so the first cart load already uses the right product version.
    if (localizationLoading) return undefined;
    fetchCart();

    // A WebMCP tool call (see utils/webmcp.js) changes the cart through
    // the same API but outside React state — refetch when it fires so
    // the badge/sidebar in this tab reflect it without a page reload.
    const onExternalCartChange = () => fetchCart();
    window.addEventListener('webmcp:cart-changed', onExternalCartChange);
    return () => window.removeEventListener('webmcp:cart-changed', onExternalCartChange);
  }, [countryCode, localizationLoading]);

  const value = {
    cart,
    cartCount,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart,
    isCartOpen,
    setIsCartOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};